<script setup lang="ts">
import { useAuditRegisterStore } from '@/modules/audits/stores/useAuditRegisterStore/useAuditRegisterStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify/framework'

import type { IAuditProcessCardProps } from '@/modules/audits/stores/useAuditRegisterStore/types/IAuditProcessCardProps'

import DateMaskInput from '@/components/createdUi/DateMaskInput.vue'
import FileAttach from '@/modules/files/components/FileAttach.vue'
import FileList from '@/modules/files/components/FileList.vue'
import { AUDITS_ANCHORS } from '@/modules/onboarding/scenarios/audits/anchors'

import type { IAuditProcessPayload } from '@/modules/audits/stores/useAuditRegisterStore/types/IAuditProcessPayload'
import type { IDecision } from '@/modules/audits/stores/useAuditRegisterStore/types/IDecision'
import type { IExistingFile } from '@/modules/files/types/IExistingFile'

const props = defineProps<IAuditProcessCardProps>()
const emit = defineEmits<{
  (e: 'saved'): void
}>()
const feedbackStore = useFeedbackStore()
const auditRegisterStore = useAuditRegisterStore()

const { xs, smAndDown, mdAndDown } = useDisplay()
const route = useRoute()

// Описание предложения
const auditorComment = ref<string>('')

const decision = ref<IDecision>(null)
const gradation = ref<number | null>(null)
const category = ref<string | null>(null)
const remediationRecommendations = ref<string>('')
const remediationDeadline = ref<string | null>(null)

const gradationError = ref<boolean>(false)
const categoryError = ref<boolean>(false)
const commentError = ref<boolean>(false)

const hasChanges = computed(() => {
  if (!props.entry.status) return true

  return (
    decision.value !== props.entry.status
    || auditorComment.value !== (props.entry.comment ?? '')
    || gradation.value !== (props.entry.not_comply_level ?? null)
    || category.value !== (props.entry.not_comply_category ?? null)
    || remediationRecommendations.value !== (props.entry.remediation_recommendations ?? '')
    || remediationDeadline.value !== (props.entry.remediation_deadline ?? null)
    || files.value.length > 0
    || removedFileIds.value.length > 0
  )
})

const actionButtonText = computed(() => {
  if (!props.entry.status) {
    return 'Подтвердить выбор'
  }

  return hasChanges.value
    ? 'Изменить выбор'
    : 'Подтвердить выбор'
})

const showActionButton = computed(() => {
  // Если пункт уже заполнен и пользователь не может редактировать - не показываем кнопку
  if (props.entry.status && !props.entry.can_edit) {
    return false
  }

  return !!decision.value && hasChanges.value
})

const isEditable = computed(() => {
  // Если пункт уже заполнен - используем can_edit, иначе true
  return props.entry.status ? props.entry.can_edit : true
})

const gradationOptions = [
  { title: 'Критическое (немедленное устранение)', value: 2 },
  { title: 'Значительное (устранение в течении 10 дней)', value: 1 },
  { title: 'Незначительное (рекомендация для улучшения)', value: 0 },
]

const categoryOptions = [
  { title: 'Корректирующее действие', value: 'corrective_action' },
  { title: 'Предупреждающее действие', value: 'warning_action' },
  { title: 'Коррекция', value: 'corrective' },
]

const isGradationMenuOpen = ref(false)
const isCategoryMenuOpen = ref(false)

const selectedGradationTitle = computed(() =>
  gradationOptions.find(option => option.value === gradation.value)?.title ?? '',
)
const selectedCategoryTitle = computed(() =>
  categoryOptions.find(option => option.value === category.value)?.title ?? '',
)

const selectGradation = (value: number) => {
  gradation.value = value
  isGradationMenuOpen.value = false
}

const selectCategory = (value: string) => {
  category.value = value
  isCategoryMenuOpen.value = false
}

const choiseText = computed(() => {
  switch (decision.value) {
    case 'complies':
      return { text: 'Соответствует', color: 'success' }
    case 'does_not_comply':
      return { text: 'Не соответствует', color: 'error' }
    case 'not_applicable':
      return { text: 'Не применимо', color: 'secondary' }
    default:
      return null
  }
})

const fileAttachRef = ref<InstanceType<typeof FileAttach> | null>(null)
const cardRef = ref<HTMLElement | null>(null)

/** Клик по «пустому» месту карточки ставит фокус на неё — цель для Ctrl+V */
const onCardPointerDown = (e: MouseEvent) => {
  const target = e.target as HTMLElement | null
  if (!target) return

  if (target.closest('input, textarea, button, a, select, [contenteditable="true"], .v-btn, .v-field, .v-selection-control, .v-list-item')) {
    return
  }

  cardRef.value?.focus()
}

const updateSelectedOption = () => {
}

// Массив новых файлов для загрузки
const files = ref<File[]>([])

// Массив ID удаляемых файлов
const removedFileIds = ref<number[]>([])

/**
 * Вычисляемое свойство файлов текущей записи
 *
 * Назначение:
 * - Нормализация и фильтрация данных о прикрепленных файлах
 * - Обеспечение корректного формата данных для компонента FileList
 *
 * Возвращает:
 * - Array: массив объектов файлов с нормализованными полями
 */
const currentEntryFiles = computed((): IExistingFile[] => {
  const entry = props.entry
  const arr = Array.isArray(entry?.files) ? entry.files : []

  return arr
    .map((f: any): IExistingFile | null => {
      const id = typeof f?.id === 'number' ? f.id : Number(f?.id)
      const file = typeof f?.file === 'string' ? f.file : (f?.file?.url ?? '')
      const size = f?.size
      const filename = f?.filename

      return (Number.isFinite(id) && file)
        ? { id, file, size, filename }
        : null
    })
    .filter((x): x is IExistingFile => x !== null)
})

/**
 * Обработчик удаления существующего файла
 *
 * Назначение:
 * - Добавление ID файла в список для последующего удаления
 * - Предотвращение дублирования ID в списке удаления
 *
 * Входные данные:
 * - id: number - ID удаляемого файла
 */
const onExistingFileRemove = (id: number) => {
  if (!removedFileIds.value.includes(id)) {
    removedFileIds.value.push(id)
  }
}

/**
 * Обработчик обновления списка файлов
 *
 * Назначение:
 * - Синхронизация локального состояния файлов с дочерним компонентом
 *
 * Входные данные:
 * - v: File[] - новый массив файлов
 */
const onFilesUpdate = (v: File[]) => {
  files.value = Array.isArray(v) ? v : []
}

const markCorresponds = () => {
  decision.value = 'complies'
}

const markUncorresponds = () => {
  decision.value = 'does_not_comply'
}

const markNotApplicable = () => {
  decision.value = 'not_applicable'
}

const saveResult = async () => {
  // 1. Валидация
  if (!decision.value) return

  gradationError.value = false
  categoryError.value = false
  commentError.value = false

  if (decision.value === 'does_not_comply') {
    if (gradation.value === null) gradationError.value = true
    if (!category.value) categoryError.value = true

    if (gradationError.value || categoryError.value) {
      feedbackStore.showToast({
        type: 'error',
        message: 'Заполните градацию несоответствия и категорию действия',
      })
      return
    }

    if (!auditorComment.value || !auditorComment.value.trim()) {
      commentError.value = true

      feedbackStore.showToast({
        type: 'error',
        message: 'Заполните наблюдение аудитора',
      })

      return
    }
  }

  // 2. Сбор данных
  const auditId = Number(route.params.id)

  // Строгая типизация Payload
  const payload: IAuditProcessPayload = {
    assignment_id: props.assignment_id,
    point_id: props.entry.point.id,
    department_id: props.department_id,
    status: decision.value,
    comment: auditorComment.value,
    not_comply_category: decision.value === 'does_not_comply' ? category.value : null,
    not_comply_level: decision.value === 'does_not_comply' ? gradation.value : null,
    remediation_recommendations: decision.value === 'does_not_comply' ? (remediationRecommendations.value || null) : null,
    remediation_deadline: decision.value === 'does_not_comply' ? (remediationDeadline.value || null) : null,
    delete_files: removedFileIds.value,
    add_files: files.value,
  }

  // 3. Вызов стора
  await auditRegisterStore.submitAuditProcess(auditId, payload)

  // 4. Очистка
  files.value = []
  removedFileIds.value = []

  emit('saved')
}

watch(
  () => props.entry,
  entry => {
    if (!entry) return

    // решение
    decision.value = entry.status ?? null

    // комментарий
    auditorComment.value = entry.comment ?? ''

    // градация и категория
    gradation.value = entry.not_comply_level ?? null
    category.value = entry.not_comply_category ?? null

    // рекомендации и срок устранения
    remediationRecommendations.value = entry.remediation_recommendations ?? ''
    remediationDeadline.value = entry.remediation_deadline ?? null

    // файлы
    files.value = []
    removedFileIds.value = []
  },
  { immediate: true },
)
</script>

<template>
  <div
    ref="cardRef"
    class="d-flex flex-column gap-4 audit-check-card"
    tabindex="0"
    @mousedown="onCardPointerDown"
  >
    <!-- Первая строка: заголовок и статус с меню -->

    <div class="d-flex w-100 justify-space-between" :class="{ 'flex-column': xs, 'align-center': !xs }">
      <div class="d-flex align-center gap-2" :class="{ 'w-100 justify-space-between': xs }">
        <VBtn
          v-if="props.standard"
          :class="{ 'flex-grow-1': xs }"
          variant="outlined"
          color="secondary"
          prepend-icon="ri-delete-bin-3-line"
        >
          Норматив
        </VBtn>
      </div>
    </div>

    <div
      v-if="props.show_nonconformity"
      class="d-flex gap-2 align-center required-label"
    >
      <VIcon icon="ri-alert-line" size="16" />
      <span>{{ props.nonconformity }}</span>
    </div>

    <!-- Вопрос -->
    <div>
      <p class="body-1 suggestion-card__text">
        {{ props.point_question }}
      </p>
    </div>

    <!-- Блок отчета -->
    <div class="d-flex gap-2 flex-column" :data-onboarding="AUDITS_ANCHORS.process.comment">
      <div class="d-flex">
        <VTextarea
          v-model="auditorComment"
          :error="commentError"
          density="comfortable"
          rows="3"
          variant="outlined"
          class="flex-grow-1"
          :class="{ 'mt-2': xs }"
          persistent-placeholder
          :disabled="!isEditable"
        >
          <template #label>
            <span>Наблюдение аудитора</span>
          </template>
        </VTextarea>
      </div>

      <div v-if="isEditable" class="d-flex align-center gap-4 flex-wrap">
        <VBtn
          :class="smAndDown ? 'w-100' : 'align-self-start'"
          class="flex-shrink-1"
          color="secondary"
          prepend-icon="ri-attachment-line"
          variant="outlined"
          @click="fileAttachRef?.openDialog()"
        >
          Прикрепить файлы
        </VBtn>
        <span v-if="!smAndDown" class="text-body-2 paste-hint">
          или вставьте файлы из буфера (Ctrl+V)
        </span>
      </div>
    </div>

    <template v-if="decision === 'does_not_comply'">
      <!-- Рекомендации -->
      <div class="d-flex">
        <VTextarea
          v-model="remediationRecommendations"
          density="comfortable"
          rows="3"
          variant="outlined"
          class="flex-grow-1"
          persistent-placeholder
          :disabled="!isEditable"
        >
          <template #label>
            <span>Рекомендации</span>
          </template>
        </VTextarea>
      </div>

      <!-- Срок устранения -->
      <div>
        <DateMaskInput
          v-model="remediationDeadline"
          :disabled="!isEditable"
          class="w-100"
        >
          <template #label>
            <span>Срок устранения</span>
          </template>
        </DateMaskInput>
      </div>

      <div
        class="d-flex ga-2"
        :class="{ 'flex-column': mdAndDown }"
      >
      <div :class="mdAndDown ? 'w-100' : 'flex-1-1-0'">
        <VMenu
          v-model="isGradationMenuOpen"
          :close-on-content-click="false"
          location="bottom"
        >
          <template #activator="{ props: menuProps }">
            <VTextarea
              :append-inner-icon="isGradationMenuOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
              :error="gradationError"
              :model-value="selectedGradationTitle"
              class="invisible-readonly cursor-pointer"
              label="Градация несоответствия"
              variant="outlined"
              density="comfortable"
              rows="1"
              auto-grow
              no-resize
              readonly
              hide-details="auto"
              v-bind="menuProps"
            />
          </template>

          <VList>
            <VListItem
              v-for="option in gradationOptions"
              :key="option.value"
              :active="gradation === option.value"
              :title="option.title"
              @click="selectGradation(option.value)"
            />
          </VList>
        </VMenu>
      </div>

      <div :class="mdAndDown ? 'w-100' : 'flex-1-1-0'">
        <VMenu
          v-model="isCategoryMenuOpen"
          :close-on-content-click="false"
          location="bottom"
        >
          <template #activator="{ props: menuProps }">
            <VTextarea
              :append-inner-icon="isCategoryMenuOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
              :error="categoryError"
              :model-value="selectedCategoryTitle"
              class="invisible-readonly cursor-pointer"
              label="Категория действия"
              variant="outlined"
              density="comfortable"
              rows="1"
              auto-grow
              no-resize
              readonly
              hide-details="auto"
              v-bind="menuProps"
            />
          </template>

          <VList>
            <VListItem
              v-for="option in categoryOptions"
              :key="option.value"
              :active="category === option.value"
              :title="option.title"
              @click="selectCategory(option.value)"
            />
          </VList>
        </VMenu>
      </div>
    </div>
    </template>

    <VCheckbox
      v-if="props.show_duplicate"
      :label="props.nonconformity"
      @update:model-value="updateSelectedOption"
    />

    <!-- Блок файлов для задачи -->
    <div>
      <!-- Существующие файлы -->
      <FileList
        v-if="currentEntryFiles.length"
        :items="currentEntryFiles"
        @remove="onExistingFileRemove"
      />

      <!-- Новые файлы -->
      <FileAttach
        ref="fileAttachRef"
        :files="files"
        accept="*"
        paste-requires-focus
        @update:files="onFilesUpdate"
      />
    </div>

    <footer class="d-flex align-center flex-column gap-2">
      <div class="d-flex align-center justify-space-between w-100">
        <div>
          <span v-if="choiseText" :class="`text-${choiseText.color}`">
            {{ choiseText.text }}
          </span>
        </div>
        <div class="d-flex align-self-end justify-space-evenly gap-2" :data-onboarding="AUDITS_ANCHORS.process.decision">
          <VIconBtn
            :rounded="false"
            :size="34"
            class="rounded-sm flex-grow-0 flex-shrink-0"
            color="success"
            icon="ri-check-line"
            :variant="decision === 'complies' ? 'tonal' : 'outlined'"
            :class="{ 'mt-2': xs }"
            :disabled="!isEditable"
            @click="markCorresponds"
          />

          <VIconBtn
            :rounded="false"
            :size="34"
            class="rounded-sm flex-grow-0 flex-shrink-0"
            color="error"
            icon="ri-close-line"
            :variant="decision === 'does_not_comply' ? 'tonal' : 'outlined'"
            :class="{ 'mt-2': xs }"
            :disabled="!isEditable"
            @click="markUncorresponds"
          />

          <VIconBtn
            :rounded="false"
            :size="34"
            class="rounded-sm flex-grow-0 flex-shrink-0"
            color="secondary"
            icon="ri-subtract-line"
            :variant="decision === 'not_applicable' ? 'tonal' : 'outlined'"
            :class="{
              'mt-2': xs,
              'not-applicable-button--active': decision === 'not_applicable',
            }"
            :disabled="!isEditable"
            @click="markNotApplicable"
          />
        </div>
      </div>

      <VBtn
        v-if="showActionButton"
        :class="{ 'w-100': xs }"
        variant="outlined"
        :loading="feedbackStore.isGlobalLoading"
        class="general-button align-self-end"
        @click="saveResult"
      >
        {{ actionButtonText }}
      </VBtn>
    </footer>
  </div>
</template>

<style scoped lang="scss">
.audit-check-card {
  outline: none;
}

.not-applicable-button--active {
  background-color: rgba(var(--v-theme-secondary), var(--v-activated-opacity)) !important;
}

.paste-hint {
  color: rgba(var(--v-theme-text-primary), 0.55);
}
</style>
